import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { tosAccelerate } from '../utils/tosAccelerate';

interface ImageThumbnailProps {
  url: string;
  fileName?: string;
  size?: number;
  thumbSize?: number;
}

export default function ImageThumbnail({ url, fileName, thumbSize = 120 }: ImageThumbnailProps) {
  const { t } = useTranslation('common');
  const [zoomed, setZoomed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const alt = fileName || t('imageThumbnail.defaultAlt');

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomed(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    const trigger = triggerRef.current;
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      trigger?.focus();
    };
  }, [zoomed]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setZoomed(true)}
        aria-label={t('imageThumbnail.zoomAria', { name: alt })}
        style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', borderRadius: 4, lineHeight: 0 }}
      >
        <img
          src={tosAccelerate(url)}
          alt={alt}
          style={{ maxWidth: thumbSize, maxHeight: thumbSize, borderRadius: 4, display: 'block' }}
        />
      </button>
      {/* portal 到 body:评论 modal 等祖先带 transform 时会改写 fixed 的包含块，原地渲染会错位 */}
      {zoomed && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={() => setZoomed(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, cursor: 'zoom-out', padding: 24,
          }}
        >
          <button
            ref={closeRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            aria-label={t('imageThumbnail.close')}
            style={{
              position: 'absolute', top: 16, right: 16, width: 40, height: 40,
              borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>
          <img
            src={tosAccelerate(url)}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain',
              borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', cursor: 'default',
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
